import {ArrowDownIcon, ArrowUpIcon, ViewColumnsIcon} from "@heroicons/react/20/solid";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {OtsActionButton, OtsIconActionButton, getOtsActionButtonClassName} from "../components/OtsButton";
import {OtsPopover} from "../components/OtsPopover";
import {OtsSearchInput} from "../components/OtsSearchInput";
import {OtsSwitch} from "../components/OtsSwitch";
import {OtsThemeRoot} from "../theme/OtsTheme";
import type {ColumnLayoutControls} from "./useColumnLayout";

/** Shared controls for any table adapter using useColumnLayout. */
export function ColumnPicker({columns, setColumnVisible, moveColumn, reset}: ColumnLayoutControls) {
  const {t} = useTranslation("common");
  const [search, setSearch] = useState("");
  const visibleCount = columns.filter((column) => column.visible).length;
  const matches = columns.filter((column) =>
    `${column.columnLabel ?? column.title} ${column.group ?? ""} ${column.id}`
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  return (
    <OtsThemeRoot className="inline-flex">
      <OtsPopover
        placement="bottom end"
        density="compact"
        ariaLabel={t("grid.columns", "Columns")}
        tooltip={t("grid.columns", "Columns")}
        trigger={<ViewColumnsIcon aria-hidden="true" className="size-[18px]" />}
        buttonClassName={getOtsActionButtonClassName("text", "compact", "!size-8 !p-0")}
        panelClassName="!max-w-none w-[min(24rem,calc(100vw-1rem))]"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold" aria-live="polite">
              {t("grid.visibleColumns", {defaultValue: `${visibleCount} columns visible`, count: visibleCount})}
            </p>
            <OtsActionButton variant="text" size="mini" onClick={reset}>
              {t("grid.resetColumns", "Reset columns")}
            </OtsActionButton>
          </div>
          <OtsSearchInput
            label={t("grid.searchColumns", "Search columns")}
            size="compact"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            onClear={() => setSearch("")}
          />
          <p className="text-[var(--ots-text-muted)]">
            {t("grid.reorderColumns", "Use the arrows to reorder columns.")}
          </p>
          <div
            className="max-h-80 space-y-1 overflow-y-auto"
            role="group"
            aria-label={t("grid.availableColumns", "Available columns")}
          >
            {matches.map((column) => {
              const index = columns.findIndex((candidate) => candidate.id === column.id);
              const label = column.columnLabel ?? column.title;
              return (
                <div key={column.id} className="flex items-center gap-1 rounded-md py-0.5">
                  <OtsSwitch
                    className="min-w-0 flex-1"
                    size="compact"
                    label={label}
                    checked={column.visible}
                    disabled={column.visible && visibleCount === 1}
                    onCheckedChange={(visible) => setColumnVisible(column.id, visible)}
                  />
                  <OtsIconActionButton
                    size="mini"
                    variant="text"
                    icon={<ArrowUpIcon />}
                    label={t("grid.moveColumnLeft", {defaultValue: `Move ${label} left`, column: label})}
                    disabled={index === 0}
                    onClick={() => moveColumn(column.id, columns[index - 1].id)}
                  />
                  <OtsIconActionButton
                    size="mini"
                    variant="text"
                    icon={<ArrowDownIcon />}
                    label={t("grid.moveColumnRight", {defaultValue: `Move ${label} right`, column: label})}
                    disabled={index === columns.length - 1}
                    onClick={() => moveColumn(column.id, columns[index + 1].id)}
                  />
                </div>
              );
            })}
            {!matches.length && (
              <p className="py-2 text-[var(--ots-text-muted)]">{t("grid.noColumns", "No matching columns")}</p>
            )}
          </div>
        </div>
      </OtsPopover>
    </OtsThemeRoot>
  );
}
